import React, { useState, useEffect } from 'react';

import { FilterRule, FilterTypeButtons } from './common/FilterRule';

import { Card } from 'selfly-ui-react';

import './filter-component.scss';

export default function DeviceFilters(props) {
    const [deviceType, setDeviceTypes] = useState({
        value: {
            smartcabinet: true,
            cabinet: true,
            smartshelf: true,
            'logistics/pc': true,
            edgeserver: true,
            gatereader: true,
            handheldreader: true,
            hammer: true,
            forklift: true,
            readingpoint: true,
            intelligentinventory: true,
            other: true,
            encodingstation: true,
            printer: true,
            selflyEncode: true
        },
        negative: false,
    });
    const [deviceSubType, setDeviceSubTypes] = useState({
        value: {
            'any': true,
            'IF2': false,
            'IF3': false,
            'IF3Freezer': false,
            'IFG_3_0_S1_V1_EU': false,
            'IFG_3_0_R1S1_V1_EU': false
        },
        negative: false,
    });    
    const [deviceStatus, setDeviceStatus] = useState('both');
    const [deviceLifeCycleStatus, setDeviceLifeCycleStatus] = useState('all');
    const [deviceAlarmState, setDeviceAlarmState] = useState('all');

    const [rules, setRules] = useState([]);

    useEffect(() => {
        const rules = [];

        if (deviceLifeCycleStatus !== 'all') {
            rules.push({
                key: 'lifeCycleStatus',
                rule: lifeCycleStatus => {
                    switch (deviceLifeCycleStatus) {
                        case 'unProvisioned':
                            return lifeCycleStatus === 'INSTALLED' ||  lifeCycleStatus === 'READY';
                        case 'installed':
                            return lifeCycleStatus === 'INSTALLED';
                        case 'ready':
                            return lifeCycleStatus === 'READY';
                        case 'operational':
                            return lifeCycleStatus === 'OPERATIONAL';
                        default:
                            return false;
                    }
                },
                _value: deviceLifeCycleStatus,
            });
        }

        if (deviceAlarmState !== 'all') {
            rules.push({
                key: 'alertCount',
                rule: alerts => {
                    switch (deviceAlarmState) {
                        case 'active.criticalSeverity':
                            return alerts.active.criticalSeverity > 0;
                        case 'active.highSeverity':
                            return alerts.active.highSeverity > 0;
                        case 'active.mediumSeverity':
                            return alerts.active.mediumSeverity > 0;
                        case 'noAlarms':
                            return alerts.all === 0;
                        case 'anyAlarms':
                            return alerts.active.total > 0;
                        case 'resolvedAlarms':
                            return alerts.active.total === 0;
                        case 'activeAlarms':
                            return alerts.active.total > 0;
                        default:
                            return false;
                    }
                },
                _value: deviceAlarmState,
            });
        }

        if (deviceStatus !== 'both') {
            rules.push({
                key: 'isOnline',
                rule: 'is',
                value: deviceStatus === 'online' ? true : false,
            });
        }

        rules.push({
            key: 'type',
            rule: type => {
                const caseType = typeof type === 'string' ? type.toLowerCase() : 'unknown';
                if (deviceType.value[caseType] === undefined) {
                    return deviceType.value.other;
                }
                return deviceType.value[caseType];
            },
            _values: deviceType, // This not really used as in the filtering, but this makes sure the object has different value and triggers rerender correctly
            not: deviceType.negative,
            nonCaseSensitive: true,
        });

        rules.push({
            key: 'subType',
            rule: subType => {
                return deviceSubType.value.any ? true : deviceSubType.value[subType];
            },
            _values: deviceSubType, // This not really used as in the filtering, but this makes sure the object has different value and triggers rerender correctly
            not: deviceSubType.negative,
            nonCaseSensitive: true,
        });
        
        setRules(prev => {
            if (JSON.stringify(prev) === JSON.stringify(rules)) {
                return prev;
            }
            return rules;
        });
    }, [deviceType, deviceStatus, deviceAlarmState, deviceLifeCycleStatus, deviceSubType]);

    useEffect(() => {
        props.onChange(rules);
    }, [rules, props.onChange]);

    return (
        <div className="device-filtering-selector">
            <Card title="Device Filter">
                <FilterRule
                    name="Device Life Cycle Status"
                    help="Filter by device life cycle status"
                    typeComponent={FilterTypeButtons}
                    value={deviceLifeCycleStatus}
                    valueSet={setDeviceLifeCycleStatus}
                    options={{
                        buttons: [
                            {
                                text: 'All',
                                value: 'all',
                            },
                            {
                                text: 'Ready to provision',
                                value: 'unProvisioned',
                            },
                            {
                                text: 'Installed',
                                value: 'installed',
                            },
                            {
                                text: 'Ready',
                                value: 'ready',
                            },
                            {
                                text: 'Operational',
                                value: 'operational',
                            },
                        ],
                    }}
                />

                <FilterRule
                    name="Device Alarms"
                    help="Filter by the state of alarms for device"
                    typeComponent={FilterTypeButtons}
                    value={deviceAlarmState}
                    valueSet={setDeviceAlarmState}
                    options={{
                        buttons: [
                            {
                                text: 'All',
                                value: 'all',
                            },
                            {
                                text: 'Active Critical',
                                value: 'active.criticalSeverity',
                            },
                            {
                                text: 'Active High',
                                value: 'active.highSeverity',
                            },
                            {
                                text: 'Active Medium',
                                value: 'active.mediumSeverity',
                            },

                            {
                                text: 'Only resolved',
                                value: 'resolvedAlarms',
                            },
                            {
                                text: 'Any active',
                                value: 'activeAlarms',
                            },
                            {
                                text: 'No alarms',
                                value: 'noAlarms',
                            },
                        ],
                    }}
                />

                <FilterRule
                    name="Device Status"
                    help="Filter by device status: online, offline or both"
                    typeComponent={FilterTypeButtons}
                    value={deviceStatus}
                    valueSet={setDeviceStatus}
                    options={{
                        buttons: [
                            {
                                text: 'Both',
                                value: 'both',
                            },
                            {
                                text: 'ONLINE',
                                value: 'online',
                            },
                            {
                                text: 'OFFLINE',
                                value: 'offline',
                            },
                        ],
                    }}
                />
                <FilterRule
                    name="Device type"
                    help="Select the devices types shown."
                    typeComponent={FilterTypeButtons}
                    negative={deviceType.negative}
                    value={deviceType.value}
                    valueSet={val => {
                        setDeviceTypes({ ...val });
                    }}
                    options={{
                        multiSelect: true,
                        buttons: [
                            {
                                text: 'Intelligent Cabinet',
                                value: 'smartcabinet',
                            },
                            {
                                text: 'Printer',
                                value: 'printer',
                            },                            
                            {
                                text: 'Selfly Encode',
                                value: 'selflyEncode',
                            },
                            {
                                text: 'Encoding Station',
                                value: 'encodingstation',
                            },
                        ],
                    }}
                />
                <FilterRule
                    name="Cabinet type"
                    help="Select the cabinet type"
                    typeComponent={FilterTypeButtons}
                    negative={deviceSubType.negative}
                    value={deviceSubType.value}
                    valueSet={val => {
                        setDeviceSubTypes({ ...val });
                    }}
                    options={{
                        multiSelect: true,
                        buttons: [
                            {
                                text: 'IF2',
                                value: 'IF2',
                            },
                            {
                                text: 'IF3 RFID',
                                value: 'IF3',
                            },                            
                            {
                                text: 'IF3 Freezer',
                                value: 'IF3Freezer',
                            },
                            {
                                text: 'IF3 Hybrid',
                                value: 'IFG_3_0_R1S1_V1_EU',
                            },
                            {
                                text: 'IF3 Scale',
                                value: 'IFG_3_0_S1_V1_EU',
                            },
                            {
                                text: 'Any',
                                value: 'any'
                            }
                        ],
                    }}
                />                
            </Card>
        </div>
    );
}
